import { TradeSide } from "@foxone/api/src/models/market";
import {
  USDT_ASSET_ID,
  USDT_META,
  USDTO_ASSET_ID,
  USDTO_META,
  USDTT_ASSET_ID,
  USDTT_META
} from "trade/constants";

function replaceUsdtAsset(asset) {
  if (asset.asset_id === USDT_ASSET_ID) {
    return { ...asset, ...USDT_META };
  }
  if (asset.asset_id === USDTO_ASSET_ID) {
    return { ...asset, ...USDTO_META };
  }
  if (asset.asset_id === USDTT_ASSET_ID) {
    return { ...asset, ...USDTT_META };
  }
  return asset;
}

function replaceUsdtMeta(pair) {
  pair.base = replaceUsdtAsset(pair.base);
  pair.quote = replaceUsdtAsset(pair.quote);
  return pair;
}

function getAutoStrategyPersetOptions(vue) {
  return {
    [TradeSide.ASK]: [
      {
        value: "priceFrist",
        text: vue.$t("trade.price-first"),
        priceLimit: 0.99,
        priceChange: 0.005
      },
      {
        value: "balance",
        text: vue.$t("trade.balance"),
        priceLimit: 0.97,
        priceChange: "0.015"
      },
      {
        value: "dealFrist",
        text: vue.$t("trade.deal-frist"),
        priceLimit: 0.95,
        priceChange: 0.03
      }
    ],
    [TradeSide.BID]: [
      {
        value: "priceFrist",
        text: vue.$t("trade.price-first"),
        priceLimit: 1.01,
        priceChange: 0.005
      },
      {
        value: "balance",
        text: vue.$t("trade.balance"),
        priceLimit: 1.03,
        priceChange: 0.005
      },
      {
        value: "dealFrist",
        text: vue.$t("trade.deal-frist"),
        priceLimit: 1.05,
        priceChange: 0.005
      }
    ]
  };
}

function getAutoTradeStrategies(vue: Vue) {
  return [
    {
      strategy: "F1EX",
      text: vue.$t("trade.auto.sell"),
      side: TradeSide.ASK,
      offerAssetLabel: vue.$t("trade.auto.sell.offer"),
      description: vue.$t("trade.auto.sell.description"),
      presetOptions: getAutoStrategyPersetOptions(vue).ASK,
      priceChangeLabel: vue.$t("trade.auto.sell.price.change"),
      priceLimitLabel: vue.$t("trade.auto.sell.price.limit.label"),
      options: [
        {
          field: "side",
          value: TradeSide.ASK
        },
        {
          field: "price_change",
          value: "",
          input: true,
          label: vue.$t("trade.auto.sell.price.change"),
          unit: "%",
          min: "1",
          max: "10",
          precision: 2,
          type: "number",
          hint: (vue, min, max) => {
            if (min || max) {
              return vue.$t("common.amount.limit", { min, max });
            }
          },
          rules: (vue, min, max, unit) => {
            return [
              v => !!v || vue.$t("trade.auto.sell.price.change.required"),
              v => {
                if (min && +v < +min) {
                  return vue.$t("common.amount.min", { min: min + unit });
                }
                if (max && +v > +max) {
                  return vue.$t("common.amount.max", { max: max + unit });
                }
                return true;
              }
            ];
          },
          transfrom: v => (v / 100).toFixed(2)
        },
        {
          field: "price_limit",
          type: "number",
          value: "",
          input: true,
          label: vue.$t("trade.auto.sell.price.limit.label"),
          min: vue => {
            if (vue.pair) {
              const price = vue.pair.price_sell;
              const precision = vue.pair.price_precision;
              return vue.$common.utils.number.toPrecision(
                0.1 * price,
                precision
              );
            }
          },
          max: vue => {
            if (vue.pair) {
              const price = vue.pair.price_sell;
              const precision = vue.pair.price_precision;
              return vue.$common.utils.number.toPrecision(
                1.2 * price,
                precision
              );
            }
          },
          unit: vue => {
            return (vue.pair && vue.pair.quote && vue.pair.quote.symbol) || "-";
          },
          hint: (vue, min, max) => {
            return vue.$t("common.price.limit", { min, max });
          },
          rules: (vue, min, max, unit) => {
            return [
              v => !!v || vue.$t("trade.auto.buy.price.limit.required"),
              v => {
                if (min && +v < +min) {
                  return vue.$t("common.amount.min", { min: min + unit });
                }
                if (max && +v > +max) {
                  return vue.$t("common.amount.max", { max: max + unit });
                }
                return true;
              }
            ];
          }
        }
      ]
    },
    {
      strategy: "F1EX",
      text: vue.$t("trade.auto.buy"),
      side: TradeSide.BID,
      disabled: true,
      offerAssetLabel: vue.$t("trade.auto.buy.offer"),
      description: vue.$t("trade.auto.buy.description"),
      presetOptions: getAutoStrategyPersetOptions(vue).BID,
      priceChangeLabel: vue.$t("trade.auto.buy.price.change"),
      priceLimitLabel: vue.$t("trade.auto.buy.price.limit.label"),
      options: [
        {
          field: "side",
          value: TradeSide.BID
        },
        {
          field: "price_change",
          value: "1",
          input: true,
          label: vue.$t("trade.auto.buy.price.change"),
          unit: "%",
          min: "1",
          max: "10",
          precision: 2,
          type: "number",
          hint: (vue, min, max) => {
            if (min || max) {
              return vue.$t("common.amount.limit", { min, max });
            }
          },
          rules: (vue, min, max, unit) => {
            return [
              v => !!v || vue.$t("trade.auto.buy.price.change.required"),
              v => {
                if (min && +v < +min) {
                  return vue.$t("common.amount.min", { min: min + unit });
                }
                if (max && +v > +max) {
                  return vue.$t("common.amount.max", { max: max + unit });
                }
                return true;
              }
            ];
          },
          transfrom: v => (v / 100).toFixed(2)
        },
        {
          field: "price_limit",
          label: vue.$t("trade.auto.buy.price.limit.label"),
          type: "number",
          value: "",
          input: true,
          min: vue => {
            if (vue.pair) {
              const price = vue.pair.price_buy;
              const precision = vue.pair.price_precision;
              return vue.$common.utils.number.toPrecision(
                0.8 * price,
                precision
              );
            }
          },
          max: vue => {
            if (vue.pair) {
              const price = vue.pair.price_buy;
              const precision = vue.pair.price_precision;
              return vue.$common.utils.number.toPrecision(2 * price, precision);
            }
          },
          unit: vue => {
            return vue.pair && vue.pair.symbol;
          },
          hint: (vue, min, max) => {
            return vue.$t("common.price.limit", { min, max });
          },
          rules: (vue, min, max, unit) => {
            return [
              v => !!v || vue.$t("trade.auto.buy.price.limit.required"),
              v => {
                if (min && +v < +min) {
                  return vue.$t("common.amount.min", { min: min + unit });
                }
                if (max && +v > +max) {
                  return vue.$t("common.amount.max", { max: max + unit });
                }
                return true;
              }
            ];
          }
        }
      ]
    }
  ];
}

export default {
  getAutoTradeStrategies,
  replaceUsdtMeta
};
